import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DashboardTemplate,
  DashboardTemplatesAdminService,
  PublishSummary,
} from "../services/dashboardTemplatesAdminService";
import { DashboardTemplateEditorDialog } from "./DashboardTemplateEditorDialog";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Switch } from "@/shared/components/ui/switch";
import { Input } from "@/shared/components/ui/input";
import { usePagination } from "@/shared/hooks/usePagination";
import { DataPagination } from "@/shared/components/DataPagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Search } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { DashboardDefaultLayoutsSection } from "./DashboardDefaultLayoutsSection";

function localizedName(tpl: DashboardTemplate, lang: "en" | "pt"): string {
  const n = tpl.name;
  if (typeof n === "string") return n;
  return n?.[lang] || n?.en || "—";
}

function scopeBadge(scope: DashboardTemplate["scope"]) {
  const variant: Record<DashboardTemplate["scope"], "secondary" | "default" | "outline"> = {
    superadmin: "default",
    organization: "secondary",
    product: "outline",
  };
  return <Badge variant={variant[scope]}>{scope}</Badge>;
}

export function DashboardTemplatesSettings() {
  const { t, i18n } = useTranslation();
  const lang: 'en' | 'pt' = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const queryClient = useQueryClient();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<DashboardTemplate | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<DashboardTemplate | null>(null);
  const [publishConfirm, setPublishConfirm] = useState<DashboardTemplate | null>(null);
  const [search, setSearch] = useState("");

  const { data: templates, isLoading } = useQuery({
    queryKey: ["admin-dashboard-templates"],
    queryFn: () => DashboardTemplatesAdminService.list(),
  });

  // Client-side filter: matches both PT and EN names, plus scope/type/category
  // labels so the user can narrow by any visible column.
  const filteredTemplates = useMemo(() => {
    const list = templates ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((tpl) => {
      const nameEn = (typeof tpl.name === "string" ? tpl.name : tpl.name?.en) ?? "";
      const namePt = (typeof tpl.name === "string" ? tpl.name : tpl.name?.pt) ?? "";
      const hay = [
        nameEn,
        namePt,
        tpl.scope,
        tpl.type,
        tpl.category ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [templates, search]);

  const {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    paginateData,
  } = usePagination<DashboardTemplate>({ initialPageSize: 10 });

  const pagedTemplates = useMemo(
    () => paginateData(filteredTemplates),
    [paginateData, filteredTemplates]
  );

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      DashboardTemplatesAdminService.update(id, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-dashboard-templates"] }),
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => DashboardTemplatesAdminService.softDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard-templates"] });
      setDeleteTarget(null);
      toast.success(t('dashboards.templatesAdmin.toastDeactivated', 'Template desativado'));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const publishMutation = useMutation<PublishSummary, Error, string>({
    mutationFn: (id) => DashboardTemplatesAdminService.publish(id),
    onSuccess: (summary) => {
      setPublishConfirm(null);
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard-templates"] });
      if (summary.scope === "superadmin") {
        toast.info(t('dashboards.templatesAdmin.toastSuperadminNoInstances', 'Templates superadmin não geram instâncias.'));
        return;
      }
      const scopeLabel = summary.scope === 'organization'
        ? t('dashboards.templatesAdmin.scopeLabelOrgs', 'orgs')
        : t('dashboards.templatesAdmin.scopeLabelProducts', 'produtos');
      toast.success(
        t('dashboards.templatesAdmin.toastPublishSummary', {
          defaultValue: 'Publicado: {{installed}} novos / {{skipped}} já existiam / {{failed}} falharam (em {{targets}} {{scopeLabel}}).',
          installed: summary.installed,
          skipped: summary.skipped,
          failed: summary.failed,
          targets: summary.targets,
          scopeLabel,
        }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const openCreate = () => {
    setEditing(undefined);
    setEditorOpen(true);
  };

  const openEdit = (template: DashboardTemplate) => {
    setEditing(template);
    setEditorOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder={t('dashboards.templatesAdmin.searchPlaceholder', 'Buscar por nome, scope, tipo ou categoria...')}
            className="pl-8"
          />
        </div>
        <Button onClick={openCreate} size="sm">
          <Plus className="h-4 w-4 mr-1" /> {t('dashboards.templatesAdmin.newTemplate', 'Novo template')}
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('dashboards.templatesAdmin.columnName', 'Nome')}</TableHead>
              <TableHead>{t('dashboards.templatesAdmin.columnScope', 'Scope')}</TableHead>
              <TableHead>{t('dashboards.templatesAdmin.columnType', 'Tipo')}</TableHead>
              <TableHead>{t('dashboards.templatesAdmin.columnCategory', 'Categoria')}</TableHead>
              <TableHead className="w-20">{t('dashboards.templatesAdmin.columnActive', 'Ativo')}</TableHead>
              <TableHead className="w-28 text-right">{t('dashboards.templatesAdmin.columnActions', 'Ações')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  {search.trim()
                    ? t('dashboards.templatesAdmin.emptySearch', 'Nenhum template encontrado para essa busca.')
                    : t('dashboards.templatesAdmin.emptyInitial', 'Nenhum template ainda. Clique em "Novo template" para começar.')}
                </TableCell>
              </TableRow>
            ) : (
              pagedTemplates.map((tpl) => (
                <TableRow key={tpl.id}>
                  <TableCell className="font-medium">{localizedName(tpl, lang)}</TableCell>
                  <TableCell>{scopeBadge(tpl.scope)}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{tpl.type}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {tpl.category ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={tpl.is_active}
                      onCheckedChange={(v) =>
                        toggleMutation.mutate({ id: tpl.id, is_active: v })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('dashboards.templatesAdmin.actionPublish', 'Publicar para todas as instâncias')}
                        onClick={() => setPublishConfirm(tpl)}
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('dashboards.templatesAdmin.actionEdit', 'Editar')}
                        onClick={() => openEdit(tpl)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('dashboards.templatesAdmin.actionDeactivate', 'Desativar')}
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(tpl)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {filteredTemplates.length > 0 && (
        <DataPagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={filteredTemplates.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          isLoading={isLoading}
        />
      )}

      <DashboardTemplateEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editing}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dashboards.templatesAdmin.deactivateTitle', 'Desativar template?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dashboards.templatesAdmin.deactivateDescription', 'O template será desativado (não será mais distribuído para novas organizações/produtos). Instâncias existentes em dashboards_base permanecem inalteradas.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dashboards.templatesAdmin.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {t('dashboards.templatesAdmin.deactivateConfirm', 'Desativar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!publishConfirm}
        onOpenChange={(o) => !o && setPublishConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dashboards.templatesAdmin.publishTitle', 'Publicar template?')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <span>
                {/* i18next config tem escapeValue:false (config.ts:36-37), então
                    dangerouslySetInnerHTML com {{name}} interpolado seria XSS.
                    <Trans> renderiza <strong> como JSX e {name} entra como
                    text node — seguro independente do conteúdo do template. */}
                <Trans
                  i18nKey={
                    publishConfirm?.scope === 'organization'
                      ? 'dashboards.templatesAdmin.publishDescriptionOrgs'
                      : 'dashboards.templatesAdmin.publishDescriptionProducts'
                  }
                  values={{
                    name: publishConfirm ? localizedName(publishConfirm, lang) : '',
                  }}
                  components={{ strong: <strong /> }}
                  defaults="O template <strong>{{name}}</strong> será instalado em todas as instâncias."
                />
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dashboards.templatesAdmin.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => publishConfirm && publishMutation.mutate(publishConfirm.id)}
              disabled={publishMutation.isPending}
            >
              {publishMutation.isPending
                ? t('dashboards.templatesAdmin.publishing', 'Publicando...')
                : t('dashboards.templatesAdmin.publishConfirm', 'Publicar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PBI #78540 — Platform default layouts. Aparece como seção abaixo da
          lista de templates para o superadmin gerenciar quais layouts servem
          de fallback quando user/org/product não têm preferência própria. */}
      <div className="pt-6 mt-6 border-t">
        <DashboardDefaultLayoutsSection />
      </div>
    </div>
  );
}

export default DashboardTemplatesSettings;
